import { useEffect, useMemo, useState } from 'react';
import './LandingPage.css';
import { getHierarchyMembers } from '../api/client';

const PAGE_SIZE_OPTIONS = [50, 100, 250, 500];

export default function MembersTable({ session, entityId }) {
  const { token } = session;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [ageFilter, setAgeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  const loadMembers = async (nextPage = 1, nextEntityId = entityId, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const response = await getHierarchyMembers(token, nextEntityId, { page: nextPage, perPage: nextPageSize });
      setData(response);
      setError('');
      setPage(nextPage);
      setPageSize(nextPageSize);
    } catch (err) {
      setError(err.message || 'Unable to load members');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMembers(1, entityId, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, entityId]);

  const members = data?.members ?? [];

  const parseAge = (value) => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    const age = Number.parseInt(value, 10);
    return Number.isNaN(age) ? null : age;
  };

  const filteredMembers = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return members.filter((member) => {
      const age = parseAge(member.age);
      const passesAge = ageFilter === 'all' || (age !== null && age >= 18);
      const passesSearch =
        term.length === 0 ||
        (member.name ?? '').toLowerCase().includes(term) ||
        (member.mobile ?? '').toLowerCase().includes(term) ||
        String(member.member_id ?? '').includes(term) ||
        (member.unit_name ?? '').toLowerCase().includes(term);
      return passesAge && passesSearch;
    });
  }, [members, ageFilter, searchTerm]);

  const sortedMembers = useMemo(() => {
    const list = [...filteredMembers];
    const getValue = (member) => {
      switch (sortKey) {
        case 'name':
          return member.name ?? '';
        case 'mobile':
          return member.mobile ?? '';
        case 'age':
          return parseAge(member.age) ?? -1;
        case 'total_collected':
          return Number(member.total_collected ?? 0);
        case 'last_contribution': {
          const value = member.last_contribution;
          const date = value ? new Date(value) : null;
          return date && !Number.isNaN(date.getTime()) ? date.getTime() : -Infinity;
        }
        case 'unit_name':
          return member.unit_name ?? '';
        default:
          return '';
      }
    };

    list.sort((a, b) => {
      const valueA = getValue(a);
      const valueB = getValue(b);
      if (valueA === valueB) {
        return 0;
      }
      if (valueA === undefined || valueA === null) {
        return 1;
      }
      if (valueB === undefined || valueB === null) {
        return -1;
      }
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return valueA - valueB;
      }
      return String(valueA).localeCompare(String(valueB), undefined, { numeric: true, sensitivity: 'base' });
    });

    return sortDir === 'asc' ? list : list.reverse();
  }, [filteredMembers, sortKey, sortDir]);

  const changeSort = (key) => {
    setSortKey((prevKey) => {
      if (prevKey === key) {
        setSortDir((prevDir) => (prevDir === 'asc' ? 'desc' : 'asc'));
        return prevKey;
      }
      setSortDir(key === 'name' || key === 'unit_name' ? 'asc' : 'desc');
      return key;
    });
  };

  const formatDateTime = (value) => {
    if (!value) {
      return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
  };

  const formatCurrency = (value) => `₹${Number(value || 0).toLocaleString()}`;

  const handleExport = () => {
    const headers = ['Member ID', 'Name', 'Phone', 'Age', 'Unit', 'Total collected', 'Last contribution'];
    const rows = sortedMembers.map((member) => [
      member.member_id ?? '',
      member.name ?? '',
      member.mobile ?? '',
      parseAge(member.age) ?? '',
      member.unit_name ?? '',
      Number(member.total_collected ?? 0),
      formatDateTime(member.last_contribution),
    ]);
    const csv = [headers.join(','), ...rows.map((row) => row.join(','))].join('\n');
    const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'members.csv';
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleShare = () => {
    const lines = sortedMembers.slice(0, 150).map((member, index) => [
      `${index + 1}. ${member.name ?? 'Unnamed'} (ID: ${member.member_id})`,
      `   Unit: ${member.unit_name ?? '—'}`,
      `   Phone: ${member.mobile ?? '—'}`,
      `   Age: ${parseAge(member.age) ?? '—'}`,
      `   Collected: ${formatCurrency(member.total_collected)}`,
      `   Last: ${formatDateTime(member.last_contribution)}`,
    ].join('\n'));

    const header = `Members report (${sortedMembers.length} listed)`;
    const generated = `Generated: ${new Date().toLocaleString('en-IN')}`;
    const message = [header, generated, lines.join('\n\n') || 'No members available'].join('\n\n');
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const totalPages = data?.pagination?.total_pages ?? 0;
  const entityName = data?.entity?.name ?? 'Entity';

  return (
    <section className="members-section">
      <header className="members-header">
        <div>
          <h2>Members overview</h2>
          <p className="members-subtitle">{entityName} — page {page} of {totalPages || 1}</p>
        </div>
        <div className="members-actions">
          <label className="members-filter">
            Age filter
            <select value={ageFilter} onChange={(event) => setAgeFilter(event.target.value)}>
              <option value="all">All</option>
              <option value="adults">18 and above</option>
            </select>
          </label>
          <label className="members-filter">
            Showing
            <select
              value={pageSize}
              onChange={(event) => {
                const nextSize = Number(event.target.value);
                loadMembers(1, entityId, nextSize);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option} per page
                </option>
              ))}
            </select>
          </label>
          <input
            type="search"
            placeholder="Search name, phone, unit"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <button type="button" className="btn outline" onClick={handleExport}>
            Export CSV
          </button>
          <button type="button" className="btn primary" onClick={handleShare}>
            Share to WhatsApp
          </button>
        </div>
      </header>

      {error ? (
        <div className="inline-message error">
          <span>{error}</span>
          <button type="button" className="btn outline" onClick={() => loadMembers(page)} disabled={loading}>
            Retry
          </button>
        </div>
      ) : loading ? (
        <div className="inline-message">Loading members…</div>
      ) : (
        <div className="members-table-wrapper">
          <table className="members-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => changeSort('member_id')}>Member ID</th>
                <th className="sortable" onClick={() => changeSort('name')}>Name</th>
                <th className="sortable" onClick={() => changeSort('mobile')}>Phone</th>
                <th className="sortable" onClick={() => changeSort('age')}>Age</th>
                <th className="sortable" onClick={() => changeSort('unit_name')}>Unit</th>
                <th className="sortable" onClick={() => changeSort('total_collected')}>Total collected</th>
                <th className="sortable" onClick={() => changeSort('last_contribution')}>Last contribution</th>
              </tr>
            </thead>
            <tbody>
              {sortedMembers.length ? (
                sortedMembers.map((member) => (
                  <tr key={member.member_id}>
                    <td>{member.member_id}</td>
                    <td>{member.name}</td>
                    <td>{member.mobile}</td>
                    <td>{parseAge(member.age) ?? '—'}</td>
                    <td>{member.unit_name}</td>
                    <td>{formatCurrency(member.total_collected)}</td>
                    <td>{formatDateTime(member.last_contribution)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="empty-state">No members found</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <footer className="members-pagination">
        <button type="button" className="btn outline" onClick={() => loadMembers(page - 1)} disabled={loading || page <= 1}>
          Previous
        </button>
        <span>
          Page {page} of {totalPages || 1}
        </span>
        <button
          type="button"
          className="btn outline"
          onClick={() => loadMembers(page + 1)}
          disabled={loading || (totalPages && page >= totalPages)}
        >
          Next
        </button>
      </footer>
    </section>
  );
}
